import React, { useEffect, useMemo, useState, useCallback } from "react";
import { AlertOctagon, RefreshCw, ChevronDown, ChevronUp, Users, Clock } from "lucide-react";
import { getClientErrors } from "../lib/api";

/**
 * V2.0.73 — Admin client-error viewer.
 *
 * Backs the "did my users hit a bug?" question with a real table
 * instead of shelling into Mongo. Two views in one card:
 *
 *   1. First-attempt success rate + retry count (top strip) — the
 *      single number to watch. Below 90% and Gemini is unhealthy.
 *
 *   2. Grouped-by-message rollup (main table) — patterns pop out
 *      immediately. Row expands to show the most recent raw event
 *      with stack + prompt.
 *
 * Populated by two sources server-side:
 *   - ErrorBoundary render crashes (V2.0.71)
 *   - Chat-failed cascade exhaustion (V2.0.72, prefixed `[chat-failed]`)
 */
function fmt(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return ts;
  }
}

function successRateColor(rate) {
  if (rate === null || rate === undefined) return "text-zinc-500";
  if (rate >= 0.95) return "text-emerald-400";
  if (rate >= 0.85) return "text-brass-400";
  return "text-rose-400";
}

export default function ClientErrorsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);
  const [expandedGroup, setExpandedGroup] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getClientErrors(hours, 100);
      setData(r.data);
    } catch (err) {
      console.debug("[ClientErrorsCard] load failed", err);
      setData({ error: err?.response?.data?.detail || err?.message || "Failed to load" });
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const groups = useMemo(() => data?.grouped || [], [data]);
  const recentByGroup = useMemo(() => {
    // Map every group's most-recent raw event by matching normalized-message
    // substring. Cheap heuristic — for exact match we'd need the backend to
    // return normalized_message per raw row; substring works in practice.
    const out = {};
    for (const g of groups) {
      out[g.normalized_message] =
        (data?.recent || []).find(
          (r) => (r.message || "").includes(g.normalized_message.split("—")[0].trim().slice(0, 40)),
        ) || (data?.recent || [])[0];
    }
    return out;
  }, [groups, data]);

  const rate = data?.first_attempt_success_rate;
  const rateLabel = rate === null || rate === undefined
    ? "—"
    : `${(rate * 100).toFixed(1)}%`;

  return (
    <div
      className="border border-zinc-800 bg-zinc-950/60 mb-6"
      data-testid="admin-client-errors-card"
    >
      {/* Header + control strip */}
      <div className="px-4 py-2.5 border-b border-zinc-800 bg-zinc-900/60 flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <AlertOctagon className="w-3 h-3 text-rose-400" />
          // CLIENT ERRORS
        </div>
        <div className="flex items-center gap-2">
          {[1, 6, 24, 168].map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => setHours(h)}
              className={
                "font-mono text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors " +
                (hours === h
                  ? "bg-brass-600 border-brass-600 text-zinc-950"
                  : "text-zinc-400 border-zinc-700 hover:border-brass-500 hover:text-brass-200")
              }
              data-testid={`client-errors-window-${h}h`}
            >
              {h < 24 ? `${h}h` : h === 24 ? "24h" : "7d"}
            </button>
          ))}
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="ml-1 text-zinc-400 hover:text-brass-400 disabled:opacity-50"
            data-testid="client-errors-refresh"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Top-line telemetry strip */}
      <div className="px-4 py-3 grid grid-cols-3 gap-4 border-b border-zinc-800">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
            First-Attempt Success
          </div>
          <div
            className={`font-display text-2xl mt-1 ${successRateColor(rate)}`}
            data-testid="client-errors-success-rate"
          >
            {rateLabel}
          </div>
          <div className="font-mono text-[9px] text-zinc-600 mt-0.5">
            {rate !== null && rate !== undefined
              ? rate >= 0.95 ? "Healthy" : rate >= 0.85 ? "Degraded" : "Unhealthy — investigate"
              : "no data yet"}
          </div>
        </div>
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
            Retries Needed
          </div>
          <div className="font-display text-2xl mt-1 text-zinc-100" data-testid="client-errors-retries">
            {data?.chats_needing_retry ?? "—"}
          </div>
          <div className="font-mono text-[9px] text-zinc-600 mt-0.5">
            of {data?.total_successful_chats ?? 0} successful chats
          </div>
        </div>
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
            Total Errors
          </div>
          <div
            className={`font-display text-2xl mt-1 ${(data?.total_errors || 0) > 0 ? "text-rose-400" : "text-zinc-500"}`}
            data-testid="client-errors-total"
          >
            {data?.total_errors ?? "—"}
          </div>
          <div className="font-mono text-[9px] text-zinc-600 mt-0.5">
            last {hours < 24 ? `${hours}h` : hours === 24 ? "24h" : "7d"}
          </div>
        </div>
      </div>

      {/* Grouped table */}
      {loading && !data ? (
        <div className="p-6 font-mono text-[10px] uppercase tracking-widest text-zinc-500 text-center">
          Loading…
        </div>
      ) : (data?.total_errors || 0) === 0 ? (
        <div className="p-6 font-mono text-xs text-center text-zinc-500">
          <div className="text-emerald-400 mb-1">// no errors in this window</div>
          <div className="text-zinc-600">
            The app has been running cleanly. Try inducing a failure to populate this table.
          </div>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 font-mono text-[9px] uppercase tracking-widest text-zinc-500 bg-zinc-950">
            <div className="col-span-7">Message</div>
            <div className="col-span-1 text-right">Count</div>
            <div className="col-span-2 flex items-center gap-1"><Users className="w-3 h-3" /> Users</div>
            <div className="col-span-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Last Seen</div>
          </div>
          {groups.map((g) => {
            const isChat = g.normalized_message.startsWith("[chat-failed]");
            const isExpanded = expandedGroup === g.normalized_message;
            const raw = recentByGroup[g.normalized_message];
            return (
              <div
                key={g.normalized_message}
                data-testid={`client-error-group-${g.normalized_message.slice(0, 24).replace(/[^a-z0-9]/gi, "-")}`}
                className="border-b border-zinc-800/50 hover:bg-zinc-900/40 transition-colors"
              >
                <button
                  type="button"
                  onClick={() => setExpandedGroup(isExpanded ? null : g.normalized_message)}
                  className="w-full grid grid-cols-12 gap-2 px-4 py-2.5 text-left"
                >
                  <div className="col-span-7 flex items-start gap-2 min-w-0">
                    {isExpanded ? (
                      <ChevronUp className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-brass-400" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-zinc-600" />
                    )}
                    <div className="min-w-0">
                      <div
                        className={
                          "font-mono text-[9px] uppercase tracking-widest mb-0.5 " +
                          (isChat ? "text-rose-400" : "text-brass-400")
                        }
                      >
                        {isChat ? "// chat failed" : "// render crash"}
                      </div>
                      <div className="text-xs text-zinc-200 truncate">{g.normalized_message}</div>
                    </div>
                  </div>
                  <div className="col-span-1 text-right font-mono text-sm text-zinc-100">{g.count}</div>
                  <div className="col-span-2 font-mono text-xs text-zinc-400">{g.affected_users}</div>
                  <div className="col-span-2 font-mono text-[10px] text-zinc-400">{fmt(g.last_seen)}</div>
                </button>
                {isExpanded && raw && (
                  <div className="px-4 pb-4 pt-1 space-y-2 bg-zinc-950/70">
                    <div className="grid grid-cols-2 gap-3 font-mono text-[10px]">
                      <div>
                        <div className="uppercase tracking-widest text-zinc-500">User</div>
                        <div className="text-zinc-200 mt-0.5 truncate">{raw.user_email || raw.user_id || "anonymous"}</div>
                      </div>
                      <div>
                        <div className="uppercase tracking-widest text-zinc-500">URL</div>
                        <div className="text-zinc-200 mt-0.5 truncate">{raw.url}</div>
                      </div>
                    </div>
                    <div>
                      <div className="font-mono uppercase tracking-widest text-[10px] text-zinc-500">
                        Message
                      </div>
                      <pre className="mt-1 font-mono text-[11px] text-rose-300 whitespace-pre-wrap break-all">
                        {raw.message}
                      </pre>
                    </div>
                    {raw.stack && (
                      <details>
                        <summary className="font-mono uppercase tracking-widest text-[10px] text-zinc-500 cursor-pointer hover:text-brass-400">
                          Stack
                        </summary>
                        <pre className="mt-1 font-mono text-[10px] text-zinc-400 whitespace-pre-wrap break-all">
                          {raw.stack}
                        </pre>
                      </details>
                    )}
                    {raw.component_stack && (
                      <details>
                        <summary className="font-mono uppercase tracking-widest text-[10px] text-zinc-500 cursor-pointer hover:text-brass-400">
                          Component Stack
                        </summary>
                        <pre className="mt-1 font-mono text-[10px] text-zinc-400 whitespace-pre-wrap break-all">
                          {raw.component_stack}
                        </pre>
                      </details>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
